/// <amd-module name="DistributedTask/Common/Library/AgentSpecificationDropdown" />
import * as React from "react";

import { TaskAgentQueue } from "DistributedTask/Client/Contracts/TaskAgent";
import * as Resources from "DistributedTask/Common/Library/Resources";
import { IAgentSpecification } from "DistributedTask/Common/Library/Sources/AgentSpecificationService";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { ComboBox, IComboBoxProps, IComboBoxOption } from "VSSUI/ComboBox";
import { Link } from "VSSUI/Link";

export interface IAgentSpecificationOption extends IComboBoxOption {
    item: IAgentSpecification;
}
export interface IAgentSpecificationDropdownProps {
    agentSpecifications: IAgentSpecification[];
    agentQueue: TaskAgentQueue;
    selectedAgentSpecification?: IAgentSpecification;
    errorMessage: string;
    onChanged?(agentSpecification: IAgentSpecification): void;
    disabled?: boolean;
}

export interface IAgentSpecificationDropdownState {
    isEnabled: boolean;
}

export class AgentSpecificationDropdown extends VssComponent<IAgentSpecificationDropdownProps, IAgentSpecificationDropdownState, IVssComponentContext> {

    constructor(props: IAgentSpecificationDropdownProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            isEnabled: this._isEnabled(context),
        };
    }

    public render(): JSX.Element | null {

        if (!this.state.isEnabled) {
            return null;
        }

        const options = this._getOptions();
        if (options.length === 0 && !this.props.errorMessage) {
            // hide this component if there are no options available and there isn't an error message
            return null;
        }

        const selectedKey = this.props.selectedAgentSpecification ? this.props.selectedAgentSpecification.identifier : "";
        const comboProps: IComboBoxProps = {
            options: options,
            disabled: this.props.disabled,
            label: Resources.AgentSpecificationLabel,
            errorMessage: this.props.errorMessage,
            onChanged: this._onChanged,
            allowFreeform: false,
            onRenderOption: this._getAgentSpecificationOption,
            selectedKey,
            required: true
        };

        return (<div className={"agent-specification-dropdown"}>
            <ComboBox {...comboProps} />
            {
                this._isMacAgentSpecificationSelected() &&
                <div className="agent-specification-message">
                    <span className="agent-specification-message">
                        {Resources.OptionAffectsDataStorageAndSecurity}
                    </span>
                    <Link
                        href={"https://go.microsoft.com/fwlink/?linkid=870426"}
                        target={"_blank"}
                        rel={"noopener noreferrer"}>
                        {Resources.LearnMore}
                    </Link>
                </div>
            }
        </div>);
    }

    private _isMacAgentSpecificationSelected(): boolean {
        const selectedAgentSpecification = this.props.selectedAgentSpecification;

        return !!(selectedAgentSpecification && selectedAgentSpecification.identifier && (selectedAgentSpecification.identifier.indexOf("macOS") > -1));
    }

    private _isEnabled(context: IVssComponentContext): boolean {

        if (!context.pageContext || !context.pageContext.getService) {

            return false;
        }

        const vssPageService = context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();

        return vssPageData && 
            vssPageData.isHosted && 
            context.pageContext ? isFeatureFlagEnabled(context.pageContext, "WebAccess.Build.CIWorkflow.AgentDefinition", false) : false;
    }

    private _getOptions(): IAgentSpecificationOption[] {
        let options: IAgentSpecificationOption[] = [];

        if (this.props.agentSpecifications && this.props.agentSpecifications.length > 0) {
            options = this.props.agentSpecifications.map((definition) => {
                return {
                    key: definition.identifier,
                    text: definition.identifier,
                    item: definition,
                } as IAgentSpecificationOption;
            });
        }

        if (this.props.selectedAgentSpecification && this.props.selectedAgentSpecification.identifier) {
            // make sure the selected agent specification is in the option list
            const selectedAgentSpecification = this.props.selectedAgentSpecification;
            if (!options.some(option => option.key === selectedAgentSpecification.identifier))
            {
                options.push({
                    key: selectedAgentSpecification.identifier,
                    text: selectedAgentSpecification.identifier,
                    item: selectedAgentSpecification,
                } as IAgentSpecificationOption);
            }
        }

        return options;
    }

    private _getAgentSpecificationOption = (option: IAgentSpecificationOption): JSX.Element => {
        return <span className={"agent-specification-option"}>
            {option.text}
        </span>;
    }

    private _onChanged = (option?: IAgentSpecificationOption) => {
        if (this.props.onChanged && option && option.item) {
            this.props.onChanged(option.item);
        }
    }
}
